import type { DimensionAnnotation, Point } from "@msuite/utils/types";

/**
 * The largest tilt a dimension offset may take, in degrees. A tilt of 90° would lay the
 * extension lines flat onto the measured span and stretch them to infinity, so the angle is
 * clamped well before that — at 60° they are already twice as long as the offset.
 */
export const MAX_DIMENSION_TILT = 60;

/** A dimension shorter than this many screen pixels is not drawn at all. */
const MIN_DIMENSION_LENGTH = 5;

/** How far beyond each arrow tip the chain handles sit, in screen pixels. */
const CHAIN_HANDLE_GAP = 22;

export interface DimensionGeometry {
	/** Unit vector along the measured span, from `start` to `end`. */
	ux: number;
	uy: number;
	/** The offset end of the extension line rising from `annotation.start`. */
	s2: Point;
	/** The offset end of the extension line rising from `annotation.end`. */
	e2: Point;
	/** Midpoint of the dimension line, where the label sits. */
	mx: number;
	my: number;
	/** Label rotation in degrees, kept upright. */
	textAngle: number;
	/** Arrowhead length in image units. */
	arrowSize: number;
	/** Stroke width in image units. */
	strokeWidth: number;
	/** Whether the offset is large enough for the extension lines to be worth drawing. */
	showMarkers: boolean;
}

function clampTilt(tilt: number): number {
	return Math.min(MAX_DIMENSION_TILT, Math.max(-MAX_DIMENSION_TILT, tilt));
}

/** Fold an angle into [-180, 180), so flipping an exactly horizontal span reads as 0 and not 360. */
function normalizeAngle(angle: number): number {
	return ((((angle + 180) % 360) + 360) % 360) - 180;
}

/**
 * Resolve every coordinate a dimension annotation needs to render, in image units.
 *
 * Lengths authored in screen pixels — `offset`, `strokeWidth`, the arrowheads — are divided by
 * `baseScale` so they stay the same size on screen whatever the image resolution. The `tilt`
 * slants the two extension lines away from the perpendicular while keeping the dimension line
 * parallel to the measured span, at its full length, and at the perpendicular distance `offset`:
 * both endpoints are displaced by the same vector, so tilt changes the slant and nothing else.
 *
 * @param annotation - The dimension annotation to resolve.
 * @param baseScale - Rendered width divided by intrinsic image width.
 * @returns The resolved geometry, or `null` when the span is too short to derive a direction from.
 */
export function getDimensionGeometry(
	annotation: DimensionAnnotation,
	baseScale: number,
): DimensionGeometry | null {
	const dx = annotation.end.x - annotation.start.x;
	const dy = annotation.end.y - annotation.start.y;
	const length = Math.hypot(dx, dy);
	if (length < MIN_DIMENSION_LENGTH / baseScale) return null;

	/** Unit vector along the span, and its normal. */
	const ux = dx / length;
	const uy = dy / length;
	const nx = -uy;
	const ny = ux;

	/** The perpendicular gap, plus the slide along the span that tilting the lines produces. */
	const offset = annotation.offset / baseScale;
	const tilt = clampTilt(annotation.tilt ?? 0);
	const shear = offset * Math.tan((tilt * Math.PI) / 180);
	const displaceX = nx * offset + ux * shear;
	const displaceY = ny * offset + uy * shear;

	const s2 = { x: annotation.start.x + displaceX, y: annotation.start.y + displaceY };
	const e2 = { x: annotation.end.x + displaceX, y: annotation.end.y + displaceY };

	/** Keep the label readable rather than upside down on right-to-left spans. */
	const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
	const textAngle = normalizeAngle(angle > 90 || angle < -90 ? angle + 180 : angle);

	return {
		ux,
		uy,
		s2,
		e2,
		mx: (s2.x + e2.x) / 2,
		my: (s2.y + e2.y) / 2,
		textAngle,
		arrowSize: 12 / baseScale,
		strokeWidth: annotation.strokeWidth / baseScale,
		showMarkers: Math.abs(annotation.offset) > 2,
	};
}

/**
 * Where the two chain handles of a dimension sit: just beyond each arrow tip, continuing the
 * direction of the dimension line. The arrow tips are the points a chained dimension attaches
 * to, so the handles sit next to what they act on, and clear of the drag handles that live back
 * on the measured points.
 *
 * The gap is divided by the combined scale rather than `baseScale`, so the handles keep their
 * distance from the tips while the user zooms — the same convention the handles themselves use
 * for their radius.
 *
 * @param geometry - The resolved geometry of the dimension the handles belong to.
 * @param combinedScale - `baseScale` multiplied by the current zoom.
 * @returns A point just outside each arrow tip.
 */
export function getDimensionChainAnchors(
	geometry: DimensionGeometry,
	combinedScale: number,
): { start: Point; end: Point } {
	const gap = CHAIN_HANDLE_GAP / combinedScale;
	const ux = geometry.ux * gap;
	const uy = geometry.uy * gap;

	return {
		start: { x: geometry.s2.x - ux, y: geometry.s2.y - uy },
		end: { x: geometry.e2.x + ux, y: geometry.e2.y + uy },
	};
}
